/**
 * Welcome popup — ニュースレター登録 → WELCOME クーポン表示
 * 仕様: docs/custom-welcome-popup-design-spec.md
 * markup / config: snippets/custom-welcome-popup.liquid
 *
 * 状態は localStorage の 2 キーだけ:
 *   ark:welcome:dismissed   閉じた時刻 (ms)。dismissDays 経つまで出さない
 *   ark:welcome:subscribed  '1' なら以後ずっと出さない
 * 加えて sessionStorage の ark:welcome:shown で「同じセッションで 2 回開かない」。
 * (閉じずにページ遷移した人に毎ページ出すのを防ぐ。閉じた人は dismissed で止まる)
 *
 * 送信は Shopify 標準 customer form を fetch で投げる。
 *   成功: レスポンス URL に customer_posted=true
 *   reCAPTCHA: /challenge に飛ばされる → 通常送信に切り替え (ark:welcome:pending を立てる)
 *             戻ってきたページで customer_posted=true && pending なら成功画面を開く
 *   失敗: レスポンス HTML の [data-ark-welcome-server-error] の文面を出す
 *
 * テーマエディタ (designMode) では preview ON のときだけ即時に開き、storage は触らない。
 */
(function () {
  'use strict';

  var CFG = window.ARK_WELCOME || {};
  var KEY_DISMISSED = 'ark:welcome:dismissed';
  var KEY_SUBSCRIBED = 'ark:welcome:subscribed';
  var KEY_PENDING = 'ark:welcome:pending';
  var KEY_SESSION = 'ark:welcome:shown';
  var DAY_MS = 24 * 60 * 60 * 1000;
  var RETRY_MS = 15000; // 別の ark-modal (garage / wishlist) が開いている間は待つ

  var root, card, form, emailInput, submitBtn, submitLabel, errorEl, copyBtn, copyLabel, codeEl;
  var views = {};
  var lastFocus = null;
  var opened = false;
  var armed = false;
  var delayTimer = null;

  /* ---------- storage (private mode 等で例外になるので握る) ---------- */

  function lsGet(k) {
    try {
      return window.localStorage.getItem(k);
    } catch {
      return null;
    }
  }
  function lsSet(k, v) {
    try {
      window.localStorage.setItem(k, v);
    } catch {
      /* noop */
    }
  }
  function lsDel(k) {
    try {
      window.localStorage.removeItem(k);
    } catch {
      /* noop */
    }
  }
  function ssGet(k) {
    try {
      return window.sessionStorage.getItem(k);
    } catch {
      return null;
    }
  }
  function ssSet(k, v) {
    try {
      window.sessionStorage.setItem(k, v);
    } catch {
      /* noop */
    }
  }

  /* ---------- analytics (custom-analytics.js があれば乗る) ---------- */

  function track(name, params) {
    if (!window.ArkAnalytics || typeof window.ArkAnalytics.track !== 'function') return;
    window.ArkAnalytics.track(name, params || {});
  }

  /* ---------- 表示可否 ---------- */

  function suppressed() {
    if (CFG.designMode) return !CFG.preview;
    if (lsGet(KEY_SUBSCRIBED) === '1') return true;
    var dismissed = parseInt(lsGet(KEY_DISMISSED) || '0', 10);
    var days = Number(CFG.dismissDays);
    if (!isFinite(days) || days < 1) days = 30;
    if (dismissed && Date.now() - dismissed < days * DAY_MS) return true;
    if (ssGet(KEY_SESSION) === '1') return true;
    return false;
  }

  function anotherModalOpen() {
    return !!(window.__arkScrollLock && window.__arkScrollLock.count > 0);
  }

  /* ---------- open / close ---------- */

  function showView(name) {
    Object.keys(views).forEach(function (k) {
      views[k].hidden = k !== name;
    });
  }

  function focusable() {
    return card.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
  }

  function open(view, reason) {
    if (opened) return;
    if (anotherModalOpen()) {
      // 車種ガレージやウィッシュリストの上に被せない。少し待って出し直す
      delayTimer = setTimeout(function () {
        open(view, reason);
      }, RETRY_MS);
      return;
    }
    opened = true;
    showView(view || 'offer');
    root.hidden = false;
    if (!CFG.designMode) ssSet(KEY_SESSION, '1');
    if (window.__arkScrollLock) window.__arkScrollLock.lock();
    lastFocus = document.activeElement;
    var target = view === 'done' ? card.querySelector('[data-ark-welcome-apply]') : emailInput;
    if (target && target.focus) {
      // iOS でキーボードが即出るのを避けるため、メール欄には autofocus しない
      if (target === emailInput && isTouch())
        target = card.querySelector('[data-ark-welcome-dismiss]:not(.ark-modal__backdrop)');
      if (target) target.focus({ preventScroll: true });
    }
    document.addEventListener('keydown', onKeydown);
    track('welcome_popup_open', { view: view || 'offer', trigger: reason || 'unknown' });
  }

  function close(reason) {
    if (root.hidden) return;
    root.hidden = true;
    if (window.__arkScrollLock) window.__arkScrollLock.unlock();
    document.removeEventListener('keydown', onKeydown);
    if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
    if (reason === 'dismiss' && !CFG.designMode) {
      lsSet(KEY_DISMISSED, String(Date.now()));
      track('welcome_popup_dismiss', { view: currentView() });
    }
  }

  function currentView() {
    var keys = Object.keys(views);
    for (var i = 0; i < keys.length; i++) {
      if (!views[keys[i]].hidden) return keys[i];
    }
    return 'offer';
  }

  function onKeydown(e) {
    if (e.key === 'Escape') {
      close('dismiss');
      return;
    }
    if (e.key !== 'Tab') return;
    var f = focusable();
    if (!f.length) return;
    var first = f[0];
    var last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function isTouch() {
    return window.matchMedia && window.matchMedia('(hover: none) and (pointer: coarse)').matches;
  }

  /* ---------- 登録 ---------- */

  function showError(msg) {
    errorEl.textContent = msg;
    errorEl.hidden = false;
  }
  function clearError() {
    errorEl.textContent = '';
    errorEl.hidden = true;
  }
  function setBusy(busy) {
    submitBtn.disabled = busy;
    submitBtn.setAttribute('aria-busy', busy ? 'true' : 'false');
    submitLabel.textContent = busy ? '送信中…' : submitLabel.getAttribute('data-label');
  }

  function markSubscribed(how) {
    lsSet(KEY_SUBSCRIBED, '1');
    lsDel(KEY_PENDING);
    lsDel(KEY_DISMISSED);
    track('welcome_popup_subscribe', { method: how });
  }

  function onSubmit(e) {
    e.preventDefault();
    clearError();
    var email = (emailInput.value || '').trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      showError('メールアドレスの形式を確認してください。');
      emailInput.focus();
      return;
    }
    if (CFG.designMode) {
      // エディタではサーバーに投げない。見た目の確認だけ
      showView('done');
      return;
    }
    if (!window.fetch || !window.FormData) {
      nativeSubmit();
      return;
    }
    setBusy(true);
    // Shopify のスパム対策 (hCaptcha) が submit を横取りして通常送信に変えることがある。
    // その場合はページが customer_posted=true で戻ってくるので、先に pending を立てておく。
    lsSet(KEY_PENDING, '1');
    var body = new FormData(form);
    fetch(form.getAttribute('action') || '/contact', {
      method: 'POST',
      body: body,
      credentials: 'same-origin',
      redirect: 'follow',
      headers: { Accept: 'text/html' },
    })
      .then(function (res) {
        var url = res.url || '';
        if (url.indexOf('/challenge') !== -1) {
          // reCAPTCHA。fetch では解けないので通常送信に切り替える
          nativeSubmit();
          return null;
        }
        if (url.indexOf('customer_posted=true') !== -1) {
          return { ok: true };
        }
        return res.text().then(function (html) {
          return { ok: false, html: html, status: res.status };
        });
      })
      .then(function (result) {
        if (!result) return;
        setBusy(false);
        if (result.ok) {
          markSubscribed('fetch');
          showView('done');
          var apply = card.querySelector('[data-ark-welcome-apply]');
          if (apply) apply.focus({ preventScroll: true });
          return;
        }
        lsDel(KEY_PENDING);
        showError(
          extractServerError(result.html) ||
            '登録できませんでした。時間をおいてもう一度お試しください。'
        );
        track('welcome_popup_subscribe_failed', { status: String(result.status || '') });
      })
      .catch(function () {
        setBusy(false);
        lsDel(KEY_PENDING);
        showError('通信に失敗しました。時間をおいてもう一度お試しください。');
        track('welcome_popup_subscribe_failed', { status: 'network' });
      });
  }

  function extractServerError(html) {
    if (!html) return '';
    try {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var el = doc.querySelector('[data-ark-welcome-server-error]');
      return el ? el.textContent.replace(/\s+/g, ' ').trim() : '';
    } catch {
      return '';
    }
  }

  function nativeSubmit() {
    // 戻ってきたページで customer_posted=true を見て成功画面を開く
    lsSet(KEY_PENDING, '1');
    form.removeEventListener('submit', onSubmit);
    form.submit();
  }

  /* ---------- コピー ---------- */

  function onCopy() {
    var code = codeEl.textContent.trim();
    var done = function () {
      copyBtn.setAttribute('data-copied', 'true');
      copyLabel.textContent = 'コピーしました';
      track('welcome_popup_copy', {});
      setTimeout(function () {
        copyBtn.removeAttribute('data-copied');
        copyLabel.textContent = 'コピー';
      }, 2000);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code).then(done, function () {
        selectCode();
      });
    } else {
      selectCode();
    }
  }
  function selectCode() {
    var range = document.createRange();
    range.selectNodeContents(codeEl);
    var sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
  }

  /* ---------- トリガー ---------- */

  function arm() {
    if (armed) return;
    armed = true;
    var delay = Number(CFG.delaySec);
    if (!isFinite(delay) || delay < 0) delay = 6;
    delayTimer = setTimeout(function () {
      open('offer', 'delay');
    }, delay * 1000);

    var pct = Number(CFG.scrollPct);
    if (isFinite(pct) && pct > 0) {
      var onScroll = function () {
        if (opened) {
          window.removeEventListener('scroll', onScroll);
          return;
        }
        var doc = document.documentElement;
        var max = doc.scrollHeight - window.innerHeight;
        if (max <= 0) return;
        var ratio = (window.pageYOffset || doc.scrollTop) / max;
        if (ratio * 100 >= pct) {
          window.removeEventListener('scroll', onScroll);
          if (delayTimer) clearTimeout(delayTimer);
          open('offer', 'scroll');
        }
      };
      window.addEventListener('scroll', onScroll, { passive: true });
    }
  }

  /* ---------- boot ---------- */

  function boot() {
    root = document.querySelector('[data-ark-welcome]');
    if (!root) return;
    card = root.querySelector('.ark-welcome__card');
    form = root.querySelector('#ark-welcome-form');
    emailInput = root.querySelector('#ark-welcome-email');
    submitBtn = root.querySelector('[data-ark-welcome-submit]');
    submitLabel = root.querySelector('[data-ark-welcome-submit-label]');
    errorEl = root.querySelector('[data-ark-welcome-error]');
    copyBtn = root.querySelector('[data-ark-welcome-copy]');
    copyLabel = root.querySelector('[data-ark-welcome-copy-label]');
    codeEl = root.querySelector('[data-ark-welcome-code]');
    root.querySelectorAll('[data-ark-welcome-view]').forEach(function (el) {
      views[el.getAttribute('data-ark-welcome-view')] = el;
    });
    if (!form || !emailInput || !submitBtn || !errorEl) return;
    submitLabel.setAttribute('data-label', submitLabel.textContent);

    root.querySelectorAll('[data-ark-welcome-dismiss]').forEach(function (el) {
      el.addEventListener('click', function () {
        close('dismiss');
      });
    });
    form.addEventListener('submit', onSubmit);
    // hCaptcha の横取りが submit より前 (capture) で止める場合に備えて、
    // 送信ボタン押下の時点でも pending を立てる (値が空なら立てない)
    submitBtn.addEventListener('click', function () {
      if ((emailInput.value || '').indexOf('@') !== -1) lsSet(KEY_PENDING, '1');
    });
    if (copyBtn) copyBtn.addEventListener('click', onCopy);
    var apply = root.querySelector('[data-ark-welcome-apply]');
    if (apply) {
      apply.addEventListener('click', function () {
        track('welcome_popup_apply', {});
      });
    }

    // 通常送信 (reCAPTCHA 経由) から戻ってきた
    var posted = /[?&]customer_posted=true/.test(window.location.search);
    if (posted && lsGet(KEY_PENDING) === '1') {
      markSubscribed('native');
      open('done', 'posted');
      return;
    }
    if (posted) {
      // フッターのフォーム等から登録した人。以後は出さない
      lsSet(KEY_SUBSCRIBED, '1');
      return;
    }
    // サーバー側エラー付きで戻ってきた (通常送信が失敗した)
    var serverErr = root.querySelector('[data-ark-welcome-server-error]');
    if (serverErr && lsGet(KEY_PENDING) === '1') {
      lsDel(KEY_PENDING);
      open('offer', 'posted-error');
      return;
    }

    if (suppressed()) return;
    if (CFG.designMode) {
      open('offer', 'preview');
      return;
    }
    arm();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
